import type { FloorDto } from "@klantpunt/shared";
import kpBoldUrl from "@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-latin-700-normal.woff?url";
import kpLightUrl from "@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-latin-300-normal.woff?url";
import kpRegularUrl from "@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-latin-400-normal.woff?url";
import type { MeResponse } from "@klantpunt/shared";
import boldUrl from "../../assets/fonts/ChakraPetch-Bold.ttf?url";
import lightUrl from "../../assets/fonts/ChakraPetch-Light.ttf?url";
import regularUrl from "../../assets/fonts/ChakraPetch-Regular.ttf?url";
import klantpuntSvg from "../../../../../brand/klantpunt/logo.svg?raw";
import pwCoverSvg from "../../../../../brand/logo-wit-oranje.svg?raw";
import pwPageSvg from "../../../../../brand/logo-alternatief-op-wit.svg?raw";
import { loadPlanPdf } from "../../lib/pdf";
import { photoUrl } from "../../lib/photos";
import type { PlanRaster, ReportAssets } from "./buildReport";
import { reportStyle } from "./styles";

/** Browserkant van het rapport: fonts en logo per huisstijl (meegeleverd, geen CDN), tekeningen en foto's als JPEG. */

const bytes = async (url: string) => {
  const r = await fetch(url, { credentials: "same-origin" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
};

type OrgBrand = Pick<NonNullable<MeResponse["org"]>, "name" | "reportBrand" | "reportAccent" | "reportLogoFileId">;

/** Eigen logo ophalen; PNG of JPEG herkend aan de eerste bytes. Mislukt het, dan het standaardlogo. */
async function loadLogo(fileId: string) {
  try {
    const b = await bytes(photoUrl(fileId));
    const png = b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;
    const jpg = b[0] === 0xff && b[1] === 0xd8;
    return png || jpg ? { bytes: b, mime: png ? ("image/png" as const) : ("image/jpeg" as const) } : null;
  } catch {
    return null;
  }
}

/** Lettertypen, logo's en kleur volgens de huisstijl van de organisatie (standaard Klantpunt). */
export async function loadAssets(org: OrgBrand | null | undefined): Promise<ReportAssets> {
  const brand = org?.reportBrand ?? "klantpunt";
  const urls = brand === "pandwachters" ? [boldUrl, regularUrl, lightUrl] : [kpBoldUrl, kpRegularUrl, kpLightUrl];
  const [[bold, regular, light], logo] = await Promise.all([
    Promise.all(urls.map(bytes)) as Promise<[Uint8Array, Uint8Array, Uint8Array]>,
    org?.reportLogoFileId ? loadLogo(org.reportLogoFileId) : Promise.resolve(null),
  ]);
  return {
    fonts: { bold, regular, light },
    style: reportStyle(
      brand,
      { pandwachtersCover: pwCoverSvg, pandwachtersPage: pwPageSvg, klantpunt: klantpuntSvg },
      { name: org?.name ?? "", accent: org?.reportAccent ?? null, logo },
    ),
  };
}

const toJpeg = (c: HTMLCanvasElement, q: number) =>
  new Promise<Uint8Array>((resolve, reject) =>
    c.toBlob(
      (b) => (b ? b.arrayBuffer().then((a) => resolve(new Uint8Array(a)), reject) : reject(new Error("toBlob"))),
      "image/jpeg",
      q,
    ),
  );

function whiteCanvas(w: number, h: number) {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, c.width, c.height);
  return { c, ctx };
}

/** Tekening als raster van max. 2400 px breed (en ±9 Mpx), dezelfde verhouding als in de viewer. */
export async function loadPlanRaster(floor: FloorDto): Promise<PlanRaster | null> {
  const plan = floor.plan;
  if (!plan) return null;
  const fit = (w: number, h: number) => Math.min(2400 / w, Math.sqrt(9e6 / (w * h)));
  let c: HTMLCanvasElement;
  if (plan.mime === "application/pdf") {
    const doc = await loadPlanPdf(plan.id);
    const page = await doc.getPage(Math.min(floor.planPage || 1, doc.numPages));
    const base = page.getViewport({ scale: 1 });
    const vp = page.getViewport({ scale: fit(base.width, base.height) });
    const out = whiteCanvas(Math.floor(vp.width), Math.floor(vp.height));
    await page.render({ canvas: out.c, canvasContext: out.ctx, viewport: vp }).promise;
    c = out.c;
  } else {
    const r = await fetch(`/api/files/${plan.id}/url`, { credentials: "same-origin" });
    if (!r.ok) return null;
    const bmp = await createImageBitmap(await r.blob());
    const s = Math.min(1, fit(bmp.width, bmp.height));
    const out = whiteCanvas(bmp.width * s, bmp.height * s);
    out.ctx.drawImage(bmp, 0, 0, out.c.width, out.c.height);
    bmp.close();
    c = out.c;
  }
  return { jpeg: await toJpeg(c, 0.85), width: c.width, height: c.height };
}

/** Foto bijgesneden op 4:3 (1200×900), zoals in het prototype. */
export async function loadPhoto4x3(fileId: string): Promise<Uint8Array | null> {
  const r = await fetch(photoUrl(fileId), { credentials: "same-origin" });
  if (!r.ok) return null;
  const bmp = await createImageBitmap(await r.blob(), { imageOrientation: "from-image" });
  const { c, ctx } = whiteCanvas(1200, 900);
  const s = Math.max(1200 / bmp.width, 900 / bmp.height);
  const w = bmp.width * s;
  const h = bmp.height * s;
  ctx.drawImage(bmp, (1200 - w) / 2, (900 - h) / 2, w, h);
  bmp.close();
  return toJpeg(c, 0.8);
}

export function download(bytes: Uint8Array, filename: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
